import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import { useGetDashboardStats } from '../../services/loanService';
import { formatCurrency } from '../../utils/formatters';

const Dashboard = () => {
    const user = useSelector((state) => state.auth.user);

    const { data: dashboardStats, isLoading } = useGetDashboardStats({
        targetMonth: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD for Java LocalDate
        userId: user?.email,
        groupId: user?.userGroup
    }, {
        enabled: !!(user?.email && user?.userGroup) // Only run when we have both email and userGroup
    });

    if (isLoading) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3 page-fade-in">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card card-enhanced">
                                <div className="card-body text-center py-5">
                                    <div className="loading-spinner-enhanced mx-auto mb-3"></div>
                                    <p className="mt-3 text-muted loading-pulse">Loading your dashboard...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Get current month for display
    const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

    // Calculate collection rate
    const collectionRate = dashboardStats?.monthlyExpectedAmount > 0
        ? ((dashboardStats?.monthlyAmountCollected || 0) / dashboardStats?.monthlyExpectedAmount * 100).toFixed(1)
        : 0;

    // Chart configurations using CSS variables for consistent theming
    const monthlyTrendsChart = {
        series: [{
            name: 'Expected',
            data: [dashboardStats?.monthlyExpectedAmount || 0]
        }, {
            name: 'Collected',
            data: [dashboardStats?.monthlyAmountCollected || 0]
        }, {
            name: 'Remaining',
            data: [dashboardStats?.monthlyRemainingDue || 0]
        }],
        options: {
            chart: {
                type: 'bar',
                height: 300,
                toolbar: { show: false }
            },
            colors: ['#00FF00', '#00CC00', '#000000'], // Brand colors: Neon Green, Dark Green, Black
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                    borderRadius: 8,
                }
            },
            dataLabels: { enabled: false },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: [currentMonth]
            },
            yaxis: {
                labels: {
                    formatter: function (val) {
                        return 'R' + (val / 1000000).toFixed(1) + 'M';
                    }
                }
            },
            fill: { opacity: 1 },
            tooltip: {
                theme: 'light',
                style: {
                    fontSize: '14px',
                    fontFamily: 'inherit'
                },
                custom: function({ series, seriesIndex, dataPointIndex, w }) {
                    const value = series[seriesIndex][dataPointIndex];
                    const seriesName = w.globals.seriesNames[seriesIndex];
                    return `
                        <div style="
                            background: white;
                            border: 1px solid #e9ecef;
                            border-radius: 8px;
                            padding: 12px;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                            font-family: inherit;
                        ">
                            <div style="
                                font-weight: 600;
                                color: #333;
                                margin-bottom: 4px;
                                font-size: 14px;
                            ">${seriesName}</div>
                            <div style="
                                font-weight: bold;
                                color: #1a1a1a;
                                font-size: 16px;
                            ">${formatCurrency(value)}</div>
                        </div>
                    `;
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'center',
                fontSize: '14px',
                markers: {
                    width: 12,
                    height: 12,
                    radius: 6
                }
            },
            grid: {
                borderColor: 'var(--border-color)'
            }
        }
    };

    const collectionProgressChart = {
        series: [parseFloat(collectionRate)],
        options: {
            chart: {
                type: 'radialBar',
                height: 250
            },
            plotOptions: {
                radialBar: {
                    startAngle: -90,
                    endAngle: 90,
                    hollow: {
                        margin: 15,
                        size: '70%'
                    },
                    dataLabels: {
                        showOn: 'always',
                        name: {
                            offsetY: -10,
                            show: true,
                            color: '#888',
                            fontSize: '13px'
                        },
                        value: {
                            color: '#111',
                            fontSize: '24px',
                            show: true,
                            formatter: function (val) {
                                return parseInt(val) + '%';
                            }
                        }
                    }
                }
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'light',
                    shadeIntensity: 0.4,
                    inverseColors: false,
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 50, 53, 91]
                }
            },
            colors: ['var(--chart-color2)'],
            labels: ['Collection Rate']
        }
    };

    const loanStatusChart = {
        // What borrowers still owe today, by account status - adds up to the outstanding portfolio. (Amounts lent,
        // this month's cash and this month's overdue are different measures and were double counted here.)
        series: [
            dashboardStats?.outstandingCurrentValue || 0,
            dashboardStats?.outstandingInArrearsValue || 0,
            dashboardStats?.outstandingDefaultedValue || 0
        ],
        options: {
            chart: {
                type: 'donut',
                height: 300,
                background: 'transparent',
                fontFamily: 'inherit'
            },
            colors: ['#28a745', '#ffc107', '#dc3545'], // Green current, amber in arrears, red defaulted
            labels: ['Current', 'In Arrears', 'Defaulted'],
            plotOptions: {
                pie: {
                    donut: {
                        size: '70%',
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#333333',
                                offsetY: -10
                            },
                            value: {
                                show: true,
                                fontSize: '24px',
                                fontWeight: 'bold',
                                color: '#1a1a1a',
                                offsetY: 10,
                                formatter: function (val) {
                                    return formatCurrency(parseFloat(val));
                                }
                            },
                            total: {
                                show: true,
                                label: 'Outstanding Portfolio',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#666666',
                                formatter: function (w) {
                                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                    return formatCurrency(total);
                                }
                            }
                        }
                    }
                }
            },
            legend: {
                position: 'bottom',
                fontSize: '14px',
                fontWeight: 500,
                labels: {
                    colors: ['#333333'],
                    useSeriesColors: false
                },
                markers: {
                    width: 12,
                    height: 12,
                    radius: 6
                }
            },
            tooltip: {
                theme: 'light',
                style: {
                    fontSize: '14px',
                    fontFamily: 'inherit'
                },
                custom: function({ series, seriesIndex, dataPointIndex, w }) {
                    const value = series[seriesIndex];
                    const label = w.globals.labels[seriesIndex];
                    const total = series.reduce((a, b) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1);

                    return `
                        <div style="
                            background: white;
                            border: 1px solid #e9ecef;
                            border-radius: 8px;
                            padding: 12px;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                            font-family: inherit;
                            min-width: 150px;
                        ">
                            <div style="
                                font-weight: 600;
                                color: #333;
                                margin-bottom: 4px;
                                font-size: 14px;
                            ">${label}</div>
                            <div style="
                                font-weight: bold;
                                color: #1a1a1a;
                                font-size: 16px;
                                margin-bottom: 2px;
                            ">${formatCurrency(value)}</div>
                            <div style="
                                font-size: 12px;
                                color: #666;
                            ">${percentage}% of total</div>
                        </div>
                    `;
                }
            },
            dataLabels: {
                enabled: false // Disable external data labels for cleaner look
            }
        }
    };

    // Main metric cards with FontAwesome 4.x icons
    const metricCards = [
        {
            title: 'Total Loans Issued',
            subtitle: 'Total issued loans value',
            value: formatCurrency(dashboardStats?.activeLoansTotalValue || 0),
            icon: 'fa fa-money',
            color: 'success',
            trend: '↗',
            trendColor: 'text-success'
        },
        {
            title: 'Monthly Collections',
            subtitle: `Collected in ${new Date().toLocaleString('default', {month: 'short'})}`,
            value: formatCurrency(dashboardStats?.monthlyAmountCollected || 0),
            icon: 'fa fa-credit-card',
            color: 'info',
            trend: `${collectionRate}%`,
            trendColor: 'text-info'
        },
        {
            title: 'Open Leads',
            subtitle: 'Potential opportunities',
            value: dashboardStats?.openLeadsCount || 0,
            icon: 'fa fa-users',
            color: 'warning',
            trend: '→',
            trendColor: 'text-warning'
        },
        {
            title: 'Monthly Interest',
            subtitle: `Interest for ${new Date().toLocaleString('default', {month: 'short'})}`,
            value: formatCurrency(dashboardStats?.monthlyInterestAmount || 0),
            icon: 'fa fa-line-chart',
            color: 'danger',
            trend: '↑',
            trendColor: 'text-danger'
        }
    ];

    // The loan book's own month figures (book_month_figures, written by the daily loan book sync) and its targets
    const percentOf = (value, target) => target > 0 ? `${((value || 0) / target * 100).toFixed(1)}% of target` : '';
    const bookCards = [
        {
            title: 'Book Collections',
            subtitle: `Target ${formatCurrency(dashboardStats?.collectionsTarget || 0)}`,
            value: formatCurrency(dashboardStats?.bookCollectedAmount || 0),
            icon: 'fa fa-book',
            color: 'info',
            trend: percentOf(dashboardStats?.bookCollectedAmount, dashboardStats?.collectionsTarget),
            trendColor: 'text-info'
        },
        {
            title: 'Book Expected',
            subtitle: `Remaining ${formatCurrency(dashboardStats?.bookRemainingAmount || 0)}`,
            value: formatCurrency(dashboardStats?.bookExpectedAmount || 0),
            icon: 'fa fa-calendar',
            color: 'warning',
            trend: `${dashboardStats?.bookPayers ?? 0} of ${dashboardStats?.bookCustomers ?? 0} paid`,
            trendColor: 'text-warning'
        },
        {
            title: 'Book Interest',
            subtitle: `Target ${formatCurrency(dashboardStats?.interestTarget || 0)}`,
            value: formatCurrency(dashboardStats?.bookInterestAmount || 0),
            icon: 'fa fa-line-chart',
            color: 'danger',
            trend: percentOf(dashboardStats?.bookInterestAmount, dashboardStats?.interestTarget),
            trendColor: 'text-danger'
        },
        {
            title: 'Outstanding Portfolio',
            subtitle: `Old Collection Book target ${formatCurrency(dashboardStats?.oldBookTarget || 0)}`,
            value: formatCurrency(dashboardStats?.portfolioOutstandingValue || 0),
            icon: 'fa fa-pie-chart',
            color: 'success',
            trend: 'owed today',
            trendColor: 'text-success'
        }
    ];

    const renderMetricCard = (card, index) => (
                            <div key={index} className="col-xl-3 col-lg-6">
                                <div className={`stat-card-enhanced card-enhanced-stagger h-100`}
                                     style={{ animationDelay: `${index * 0.1}s` }}>
                                    <div className="card-body p-4">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className={`bg-${card.color} rounded-3 p-3 me-3 stat-icon`} style={{ 
                                                        backgroundColor: card.color === 'success' ? '#28a745' : 
                                                                      card.color === 'info' ? '#0dcaf0' : 
                                                                      card.color === 'warning' ? '#ffc107' : 
                                                                      card.color === 'danger' ? '#dc3545' : '#0d6efd'
                                                    }}>
                                                        <i className={`${card.icon} fs-3 text-white`}></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="card-title mb-0 text-dark fw-semibold">{card.title}</h6>
                                                        <small className="text-muted">{card.subtitle}</small>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-baseline justify-content-between">
                                                    <h3 className="fw-bold mb-0 text-dark">{card.value}</h3>
                                                    <span className={`badge badge-enhanced bg-light-${card.color} ${card.trendColor} fs-6 fw-semibold`}>
                                                        {card.trend}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Decorative element */}
                                    <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '4rem', margin: '1rem' }}>
                                        <i className={`${card.icon} text-${card.color}`}></i>
                                    </div>
                                </div>
                            </div>
    );

    const taskStats = [
        {
            title: 'My Tasks',
            value: dashboardStats?.userTasksCount || 0,
            icon: 'fa fa-user',
            color: 'primary',
            desc: 'Assigned to you'
        },
        {
            title: 'Group Tasks',
            value: dashboardStats?.groupTasksCount || 0,
            icon: 'fa fa-users',
            color: 'secondary',
            desc: 'Team assignments'
        }
    ];

    const quickActions = [
        { title: 'Create Lead', icon: 'fa fa-user-plus', link: '/leads', color: 'primary', desc: 'Add new customer' },
        { title: 'New Application', icon: 'fa fa-plus', link: '/loan-application/new', color: 'success', desc: 'Start application' },
        { title: 'Work Items', icon: 'fa fa-tasks', link: '/work-items', color: 'info', desc: 'Process pending' },
        { title: 'Reports', icon: 'fa fa-bar-chart', link: '/reports', color: 'warning', desc: 'View analytics' },
        { title: 'Documents', icon: 'fa fa-folder', link: '/documents', color: 'secondary', desc: 'Manage files' }
    ];

    return (
        <>
            {/* Header */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient animate-fade-in">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white hover-glow" to="/" style={{ transition: 'all 0.3s ease' }}>
                                        <i className="fa fa-home me-2"></i>Home
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Dashboard</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h1 className="fs-2 fw-bold mb-2 text-white animate-fade-in-up">
                                Welcome back{user?.name ? `, ${user.name}` : ''}! 👋
                            </h1>
                            <p className="mb-0 text-white fs-6 opacity-75 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                                Your loan portfolio overview for {currentMonth}
                            </p>
                        </div>
                        <div className="col-md-4 text-md-end">
                            <div className="d-inline-flex align-items-center bg-white text-dark rounded-pill px-4 py-2 hover-lift shadow-glow" style={{ boxShadow: '0 4px 15px rgba(0,255,0,0.2)' }}>
                                <i className="fa fa-calendar me-2" style={{ color: '#00FF00' }}></i>
                                <span className="fw-medium">{new Date().toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-4 bg-body page-fade-in">
                <div className="container-fluid">

                    {/* Key Metrics Cards - Enhanced */}
                    <div className="row g-4 mb-5">
                        {metricCards.map(renderMetricCard)}
                    </div>

                    {/* The loan book's own figures and targets for the month - next to the LMS's figures above */}
                    {dashboardStats?.figuresSource && (
                        <>
                            <h6 className="text-muted fw-semibold mb-3">Loan book figures for {currentMonth} ({dashboardStats.figuresSource})</h6>
                            <div className="row g-4 mb-5">
                                {bookCards.map(renderMetricCard)}
                            </div>
                        </>
                    )}

                    {/* Quick Actions - Enhanced */}
                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="card card-enhanced border-0 shadow-sm bg-card animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                                <div className="card-header bg-transparent border-0">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary-gradient-subtle rounded-3 p-3 me-3 hover-glow">
                                            <i className="fa fa-bolt" style={{ color: '#00FF00' }}></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0">Quick Actions</h5>
                                            <p className="text-muted mb-0 small">Common tasks and shortcuts</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row g-4">
                                        {quickActions.map((action, index) => (
                                            <div key={index} className="col-xl col-lg-4 col-md-6">
                                                <Link to={action.link} className="text-decoration-none">
                                                    <div className="card bg-light border-0 h-100 quick-action-card hover-lift" style={{ transition: 'all 0.3s ease', borderRadius: '12px' }}>
                                                        <div className="card-body text-center p-4">
                                                            <div className="text-white rounded-3 mx-auto mb-3 d-flex align-items-center justify-content-center hover-scale"
                                                                style={{ 
                                                                    width: '60px', 
                                                                    height: '60px',
                                                                    transition: 'all 0.3s ease',
                                                                    background: action.color === 'primary' 
                                                                        ? 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)'
                                                                        : action.color === 'success'
                                                                        ? '#28a745'
                                                                        : action.color === 'info'
                                                                        ? '#0dcaf0'
                                                                        : action.color === 'warning'
                                                                        ? '#ffc107'
                                                                        : '#6c757d'
                                                                }}>
                                                                <i className={`${action.icon} fs-3 text-white`}></i>
                                                            </div>
                                                            <h6 className="fw-bold mb-2 text-dark">{action.title}</h6>
                                                            <p className="text-muted mb-0 small">{action.desc}</p>
                                                            <div className="mt-2">
                                                                <i className="fa fa-arrow-right" style={{ color: '#00FF00', transition: 'transform 0.3s ease' }}></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Analytics Section */}
                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="card card-enhanced border-0 shadow-sm bg-card animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                                <div className="card-header bg-transparent border-0">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary-gradient-subtle rounded-3 p-3 me-3 hover-glow">
                                            <i className="fa fa-users" style={{ color: '#00FF00' }}></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0">Customer Payment Analytics</h5>
                                            <p className="text-muted mb-0 small">Payment behavior insights for {currentMonth}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row g-4">
                                        {/* Payment Percentage Progress - Redesigned */}
                                        <div className="col-xl-4">
                                            <div className="card h-100 bg-light border-0" style={{ borderRadius: '12px' }}>
                                                <div className="card-body p-4 text-center">
                                                    <h6 className="text-dark fw-semibold mb-4">Payment Participation</h6>
                                                    <div className="position-relative d-inline-flex align-items-center justify-content-center mb-4"
                                                         style={{ width: '160px', height: '160px' }}>
                                                        <svg width="160" height="160" className="position-absolute">
                                                            <circle
                                                                cx="80"
                                                                cy="80"
                                                                r="70"
                                                                fill="none"
                                                                stroke="#e9ecef"
                                                                strokeWidth="12"
                                                            />
                                                            <circle
                                                                cx="80"
                                                                cy="80"
                                                                r="70"
                                                                fill="none"
                                                                stroke="#28a745"
                                                                strokeWidth="12"
                                                                strokeLinecap="round"
                                                                strokeDasharray={`${(dashboardStats?.percentageOfPayers || 0) * 4.398} ${439.8 - (dashboardStats?.percentageOfPayers || 0) * 4.398}`}
                                                                transform="rotate(-90 80 80)"
                                                                style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
                                                            />
                                                        </svg>
                                                        <div className="text-center">
                                                            <h2 className="fw-bold text-success mb-0" style={{ fontSize: '2.5rem' }}>
                                                                {dashboardStats?.percentageOfPayers ? `${dashboardStats.percentageOfPayers.toFixed(0)}%` : '0%'}
                                                            </h2>
                                                            <small className="text-muted">participation</small>
                                                        </div>
                                                    </div>
                                                    <p className="text-muted mb-0 small">
                                                        Customers who made payments this month
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Customer Statistics - Redesigned */}
                                        <div className="col-xl-8">
                                            <div className="row g-3 h-100">
                                                <div className="col-md-6">
                                                    <div className="card h-100 bg-white border-start border-4 border-success"
                                                         style={{ boxShadow: '0 2px 15px rgba(0,0,0,0.06)', borderRadius: '10px' }}>
                                                        <div className="card-body p-4">
                                                            <div className="d-flex align-items-start">
                                                                <div className="bg-light-success rounded-3 p-3 me-3">
                                                                    <i className="fa fa-check-circle fs-3 text-success"></i>
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-1 text-dark fw-semibold">Active Payers</h6>
                                                                    <small className="text-muted d-block mb-2">Made payments this month</small>
                                                                    <h2 className="fw-bold mb-0 text-dark">
                                                                        {dashboardStats?.numberOfPayers || 0}
                                                                    </h2>
                                                                    <small className="text-success fw-medium">
                                                                        <i className="fa fa-arrow-up me-1"></i>
                                                                        Customers paid
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="card h-100 bg-white border-start border-4 border-info"
                                                         style={{ boxShadow: '0 2px 15px rgba(0,0,0,0.06)', borderRadius: '10px' }}>
                                                        <div className="card-body p-4">
                                                            <div className="d-flex align-items-start">
                                                                <div className="bg-light-info rounded-3 p-3 me-3">
                                                                    <i className="fa fa-users fs-3 text-info"></i>
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-1 text-dark fw-semibold">Total Customers</h6>
                                                                    <small className="text-muted d-block mb-2">With active loans</small>
                                                                    <h2 className="fw-bold mb-0 text-dark">
                                                                        {dashboardStats?.totalNumberOfPayers || 0}
                                                                    </h2>
                                                                    <small className="text-info fw-medium">
                                                                        <i className="fa fa-user me-1"></i>
                                                                        Active borrowers
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <div className="card bg-white border-0" style={{ borderRadius: '10px', border: '1px solid #e9ecef' }}>
                                                        <div className="card-body p-4">
                                                            <div className="row align-items-center">
                                                                <div className="col-auto">
                                                                    <div className="bg-light-warning rounded-3 p-3">
                                                                        <i className="fa fa-exclamation-triangle text-warning fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="col">
                                                                    <h6 className="mb-1 text-dark fw-semibold">Non-Paying Customers</h6>
                                                                    <p className="text-muted mb-0">
                                                                        <strong className="text-warning">
                                                                            {(dashboardStats?.totalNumberOfPayers || 0) - (dashboardStats?.numberOfPayers || 0)}
                                                                        </strong> customers haven't made payments this month and may need attention
                                                                    </p>
                                                                </div>
                                                                <div className="col-auto">
                                                                    <span className="badge bg-light-warning text-warning fs-6 fw-semibold">
                                                                        {dashboardStats?.percentageOfPayers ? `${(100 - dashboardStats.percentageOfPayers).toFixed(0)}%` : '100%'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="row g-4 mb-5">
                        <div className="col-xl-8">
                            <div className="card card-enhanced border-0 shadow-sm h-100 bg-card animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                                <div className="card-header bg-transparent border-0 pb-0">
                                    <div className="d-flex align-items-start justify-content-between">
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-1 d-flex align-items-center">
                                                <i className="fa fa-bar-chart me-2" style={{ color: '#00FF00' }}></i>
                                                Monthly Performance
                                            </h5>
                                            <p className="text-muted mb-0 small">Expected vs Collected vs Overdue</p>
                                        </div>
                                        <div className="ms-4">
                                            <div className="badge badge-enhanced bg-primary-gradient-subtle px-3 py-2 rounded-pill" style={{ color: '#00FF00', border: '1px solid rgba(0,255,0,0.3)' }}>
                                                <i className="fa fa-calendar me-1"></i>
                                                {currentMonth}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body pt-2">
                                    <Chart
                                        options={monthlyTrendsChart.options}
                                        series={monthlyTrendsChart.series}
                                        type="bar"
                                        height={300}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-4">
                            <div className="card card-enhanced border-0 shadow-sm h-100 bg-card animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
                                <div className="card-header bg-transparent border-0 pb-0">
                                    <h5 className="card-title mb-1 d-flex align-items-center">
                                        <i className="fa fa-pie-chart me-2" style={{ color: '#00FF00' }}></i>
                                        Collection Rate
                                    </h5>
                                    <p className="text-muted mb-0 small">Monthly collection progress</p>
                                </div>
                                <div className="card-body text-center">
                                    <Chart
                                        options={collectionProgressChart.options}
                                        series={collectionProgressChart.series}
                                        type="radialBar"
                                        height={250}
                                    />
                                    <div className="mt-3 p-3 bg-light rounded-3">
                                        <h6 className="text-muted mb-1">This Month Progress</h6>
                                        <p className="mb-0">
                                            <span className="text-success fw-bold">{formatCurrency(dashboardStats?.monthlyAmountCollected || 0)}</span>
                                            <span className="text-muted"> of </span>
                                            <span className="fw-bold">{formatCurrency(dashboardStats?.monthlyExpectedAmount || 0)}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Portfolio Distribution & Tasks */}
                    <div className="row g-4 mb-5">
                        <div className="col-xl-6">
                            <div className="card card-enhanced border-0 shadow-sm h-100 bg-card animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                                <div className="card-header bg-transparent border-0 pb-0">
                                    <h5 className="card-title mb-1 d-flex align-items-center">
                                        <i className="fa fa-pie-chart me-2" style={{ color: '#00FF00' }}></i>
                                        Portfolio Distribution
                                    </h5>
                                    <p className="text-muted mb-0 small">What borrowers still owe today, by status</p>
                                </div>
                                <div className="card-body">
                                    <Chart
                                        options={loanStatusChart.options}
                                        series={loanStatusChart.series}
                                        type="donut"
                                        height={300}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-6">
                            <div className="row g-4 h-100">
                                {taskStats.map((stat, index) => (
                                    <div key={index} className="col-12">
                                        <div className={`stat-card-enhanced card-enhanced-stagger h-100`}
                                             style={{ animationDelay: `${0.9 + index * 0.1}s` }}>
                                            <div className="card-body p-4">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className={`bg-${stat.color} rounded-3 p-3 me-3 stat-icon`} style={{ 
                                                                backgroundColor: stat.color === 'primary' ? '#0d6efd' : 
                                                                              stat.color === 'secondary' ? '#6c757d' : '#0d6efd'
                                                            }}>
                                                                <i className={`${stat.icon} fs-3 text-white`}></i>
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 text-dark fw-semibold">{stat.title}</h6>
                                                                <small className="text-muted">{stat.desc}</small>
                                                            </div>
                                                        </div>
                                                        <h2 className="fw-bold mb-0 text-dark">{stat.value}</h2>
                                                        <small className="text-muted">
                                                            {stat.value === 1 ? 'task' : 'tasks'} pending
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Decorative element */}
                                            <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '3rem', margin: '1rem' }}>
                                                <i className={`${stat.icon} text-${stat.color}`}></i>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .quick-action-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                    border: 2px solid transparent !important;
                }
                .quick-action-card:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 15px 35px rgba(0,255,0,0.15) !important;
                    border-color: #00FF00 !important;
                }
                .quick-action-card:hover .fa-arrow-right {
                    transform: translateX(5px);
                    transition: transform 0.3s ease;
                }
            `}</style>
        </>
    );
};

export default Dashboard;
